/**
 * GMT Offset Timezone Management
 *
 * Handles client-side timezone conversion using GMT offsets with server-side sync
 */

class TimezoneManager {
  constructor() {
    this.userTimezone = window.userTimezone || 'UTC';
    this.init();
  }

  init() {
    // Convert all timestamps on page load
    this.convertAllTimestamps();

    // Save timezone immediately when the profile form's timezone select changes
    const profileSelect = document.querySelector('#timezone');
    if (profileSelect) {
      profileSelect.addEventListener('change', (e) => {
        this.changeTimezone(e.target.value);
      });
    }

    // Add timezone selector to navbar
    // this.addTimezoneSelector();
  }

  parseGMTOffset(timezone) {
    if (timezone === 'UTC') return 0;

    // Parse GMT offset (e.g., "GMT-7" -> -7, "GMT+5" -> 5)
    const match = timezone.match(/GMT([+-]?\d+)/);
    return match ? parseInt(match[1]) : 0;
  }

  formatTimestamp(utcTimestamp, timezone = null) {
    timezone = timezone || this.userTimezone;
    const date = new Date(utcTimestamp * 1000);

    if (timezone === 'UTC') {
      return date.toISOString().replace('T', ' ').replace('Z', ' UTC');
    }

    // Apply GMT offset
    const offset = this.parseGMTOffset(timezone);
    const localTime = new Date(date.getTime() + (offset * 60 * 60 * 1000));

    // Format as YYYY-MM-DD HH:MM:SS TIMEZONE
    const year = localTime.getUTCFullYear();
    const month = String(localTime.getUTCMonth() + 1).padStart(2, '0');
    const day = String(localTime.getUTCDate()).padStart(2, '0');
    const hours = String(localTime.getUTCHours()).padStart(2, '0');
    const minutes = String(localTime.getUTCMinutes()).padStart(2, '0');
    const seconds = String(localTime.getUTCSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${timezone}`;
  }

  convertAllTimestamps() {
    document.querySelectorAll('[data-timestamp]').forEach(element => {
      const utcTimestamp = parseFloat(element.dataset.timestamp);
      element.textContent = this.formatTimestamp(utcTimestamp);
    });
  }


  attachTimezoneEvents() {
    const selector = document.querySelector('#timezone-selector');
    if (!selector) return;

    selector.addEventListener('click', (e) => {
      if (e.target.classList.contains('dropdown-item')) {
        e.preventDefault();

        if (e.target.id === 'auto-detect-timezone') {
          // Auto-detect browser timezone and convert to GMT offset
          const now = new Date();
          const offset = -now.getTimezoneOffset() / 60; // Convert minutes to hours and flip sign

          let gmtTimezone;
          if (offset === 0) {
            gmtTimezone = 'UTC';
          } else if (offset > 0) {
            gmtTimezone = `GMT+${offset}`;
          } else {
            gmtTimezone = `GMT${offset}`; // Already has negative sign
          }

          this.changeTimezone(gmtTimezone);
        } else {
          const timezone = e.target.dataset.timezone;
          if (timezone) {
            this.changeTimezone(timezone);
          }
        }
      }
    });
  }

  async changeTimezone(newTimezone) {
    try {
      // Save to server
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch('/account/api/timezone', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRFToken': csrfToken
        },
        body: JSON.stringify({ timezone: newTimezone })
      });

      if (response.ok) {
        await response.json();

        // Update local state
        this.userTimezone = newTimezone;
        window.userTimezone = newTimezone;

        // Update the display
        const currentTimezoneSpan = document.querySelector('#current-timezone');
        if (currentTimezoneSpan) {
          currentTimezoneSpan.textContent = newTimezone;
        }

        // Re-convert all timestamps
        this.convertAllTimestamps();

        // Show success notification
        this.showNotification(`Timezone changed to ${newTimezone}`, 'success');
      } else {
        const errorData = await response.json();
        this.showNotification(`Failed to update timezone: ${errorData.error}`, 'error');
      }
    } catch (error) {
      console.error('Failed to update timezone:', error);
      this.showNotification('Failed to update timezone. Please try again.', 'error');
    }
  }

  showNotification(message, type = 'info') {
    // Use the same Bootstrap alert pattern used elsewhere in the app
    // (see common.js) so notifications sit top-center where they are not
    // covered by the select's own dropdown.
    const alertClass = type === 'success' ? 'alert-success' : type === 'error' ? 'alert-danger' : 'alert-info';
    const icon = type === 'success' ? 'fa-check-circle' : type === 'error' ? 'fa-exclamation-circle' : 'fa-info-circle';

    const toast = document.createElement('div');
    toast.className = `alert ${alertClass} alert-dismissible fade show position-fixed start-50 translate-middle-x`;
    // Offset well below the sticky navbar (~56px in Bootstrap) so the
    // alert has breathing room. z-index above Bootstrap's sticky-top (1020).
    toast.style.top = '100px';
    toast.style.zIndex = '9999';

    const iconEl = document.createElement('i');
    iconEl.className = `fas ${icon} me-2`;
    toast.appendChild(iconEl);

    toast.appendChild(document.createTextNode(message));

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'btn-close';
    closeBtn.setAttribute('data-bs-dismiss', 'alert');
    toast.appendChild(closeBtn);

    document.body.appendChild(toast);

    // Auto-dismiss after 4 seconds
    setTimeout(() => {
      toast.remove();
    }, 4000);
  }
}

// Initialize timezone manager when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new TimezoneManager();
});